/**
 * Design tokens — the "stone and green" look, adopted 10 October 2026.
 *
 * ## This deliberately departs from the frozen prototype
 *
 * Until this date the file was a transcription of design/PROTOTYPE-SPEC.md
 * §"Colors": cream, gold, Cormorant Garamond. Emmanuel chose to replace the
 * prototype's LOOK (not its behaviour) with the direction on the design canvas
 * recorded in docs/DECISIONS.md. `design/` is still the behavioural spec and
 * the test oracle; it is no longer the visual reference.
 *
 * Plain objects and inline styles, no CSS framework (CLAUDE.md on dependencies).
 */

export const c = {
  /** Near-black green: text, the sidebar. */
  ink: '#141A16',
  inkHover: '#1F2A22',
  inkSoft: '#1C2C22',

  /** The stone ground the app sits on. */
  appBg: '#F5F5F2',
  authBg: '#E0DDD4',
  /** Raised surfaces: cards, panels. */
  card: '#FFFFFF',
  white: '#FFFFFF',
  /** Bar tracks, segmented-control backgrounds. */
  well: '#E8E8E3',

  border: '#E8E8E3',
  borderStrong: '#D2D2CC',
  borderDark: '#33473A',
  borderDarker: '#2A332C',

  /** All four pass 4.5:1 on `card` and on `appBg`. */
  muted: '#5F6259',
  muted2: '#565A51',
  label: '#3A423C',
  faint: '#6C6F66',

  /** The brand green, and the deep green of dark cards. */
  green: '#2F5640',
  greenDeep: '#1C2C22',
  /** The band around the landing and sign-in pages. */
  frame: '#2F4A3A',
  /** The one warm accent. Used small: a tick, an "expired" tag. */
  terracotta: '#C4673F',
  /**
   * Tints for tags and messages: a pale fill, a thin line of the same hue
   * round it, darker text. Lightened on 27 October 2026 (option B, "bordered
   * panels"); the old fills were heavy enough to read as blocks of colour.
   */
  amberBg: '#FCF4E2',
  amberLine: '#F0DFB4',
  amberText: '#7A5300',
  okBg: '#EAF3EC',
  okLine: '#CFE3D5',
  okText: '#24563A',

  /** A soft green for small text on dark, and a mid green for marks. */
  onDarkSoft: '#CFE0D4',
  greenMid: '#7FA58F',

  danger: '#9C4420',
  dangerBg: '#FBEEE7',
  dangerLine: '#F1D5C6',
  dangerBorder: '#E2C2B0',

  success: '#2F5640',
  success2: '#4F7A62',

  /** Text on dark green. */
  onDark: '#EDEBE4',
  onDarkBright: '#FFFFFF',
  onDarkMuted: '#A9B5AC',
  onDarkFaint: '#8F988F',
  onDarkTick: '#C9CEC7',
  wordmark: '#EDEBE4',
} as const;

/**
 * Geist, chosen by Emmanuel on 13 October 2026 from five typefaces shown on
 * the same piece of the app (docs/DECISIONS.md). It replaced Inter Tight; Sora
 * was tried first and found too wide. Large type (page titles, headline
 * figures, panel headings) uses the Light weight, exported as `light`.
 */
export const sans = "'Geist', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const light = 300;
/**
 * Chart colours, for data only. Chosen by Emmanuel from a reference dashboard
 * on 11 October 2026; brighter than the brand green so four series stay apart
 * on the stone ground. Buttons, tags and text do not use these.
 */
export const chart = {
  green: '#2E7D6B',
  greenPale: '#CFE3DB',
  orange: '#DD6B2C',
  orangePale: '#F1D3C2',
  amber: '#EDB83A',
  navy: '#23485C',
  grey: '#C9CEC7',
} as const;

/** Lot numbers, dates in tables, and the small /section/ tags. */
export const mono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

/** Large display text: light weight, tight tracking, set in `sans`. */
export const display: React.CSSProperties = {
  fontFamily: sans,
  fontWeight: light,
  letterSpacing: '-0.045em',
  lineHeight: 1.08,
};

/**
 * Shapes, adopted 13 October 2026 when Emmanuel asked for the app to borrow
 * from apple.com (docs/DECISIONS.md): fully rounded buttons, tags and tabs,
 * soft cards, and gently rounded inputs. Use these rather than a number.
 */
export const radius = { pill: 980, control: 12, card: 18 } as const;

/** The track a group of tabs sits on; the chosen tab is raised on it. */
export const segTrack: React.CSSProperties = {
  display: 'flex',
  gap: 2,
  padding: 3,
  borderRadius: radius.pill,
  background: '#E3E3DE',
  width: 'fit-content',
  maxWidth: '100%',
  flexWrap: 'wrap',
};

/** One tab on that track. */
export function segTab(active: boolean): React.CSSProperties {
  return {
    border: 'none',
    cursor: 'pointer',
    fontFamily: sans,
    fontSize: 14,
    fontWeight: 500,
    height: 34,
    padding: '0 16px',
    borderRadius: radius.pill,
    background: active ? '#FFFFFF' : 'transparent',
    color: active ? '#141A16' : '#3A423C',
    boxShadow: active ? '0 1px 2px rgba(20,26,22,0.14)' : 'none',
    whiteSpace: 'nowrap',
  };
}

export const field: React.CSSProperties = {
  width: '100%',
  height: 50,
  border: `1px solid ${c.borderStrong}`,
  borderRadius: 12,
  padding: '0 14px',
  fontSize: 16,
  fontFamily: sans,
  outline: 'none',
  background: c.white,
  boxSizing: 'border-box',
  color: c.ink,
};

export const label: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 500,
  color: c.label,
  marginBottom: 7,
};

export const primaryButton: React.CSSProperties = {
  height: 50,
  background: c.green,
  border: 'none',
  color: c.white,
  fontSize: 16,
  fontWeight: 500,
  fontFamily: sans,
  borderRadius: 980,
  cursor: 'pointer',
  width: '100%',
};
